<template>
    <div class="card mt-2">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title">Attachments</h5>
        </div>
        <b-overlay :show="showSpinner" rounded="sm">
            <ul class="list-group list-group-flush" v-if="canAddMoreAttachments">
                <li class="list-group-item">
                    <b-form-file v-model="newFiles" :multiple="allowMultipleUploads" placeholder="Choose files or drop them here..." drop-placeholder="Drop files here..." browse-text="Add..." v-on:input="uploadFile"></b-form-file>
                </li>
                <li class="list-group-item" v-for="(uploadInProgress, index) in uploadsInProgress" :key="index">
                    <div>{{ uploadInProgress.name }}</div>
                    <b-progress :value="uploadInProgress.progress" :max="100" show-progress animated></b-progress>
                </li>
            </ul>

            <b-table striped hover small :items="currentAttachments" :fields="tableFields" v-if="(currentAttachments != null) && (currentAttachments.length > 0)">

                <template v-slot:cell(fileDetails.fileName)="row">
                    <b-button v-if="fileAvailable(row.item.fileDetails)" size="sm" variant="link" :href="$urlHelper.getFileDownloadUrl(row.item.fileId)" :title="row.item.fileDetails.fileName">{{ row.item.fileDetails.fileName }}</b-button>
                    <div v-if="!fileAvailable(row.item.fileDetails)">
                        <ejs-tooltip position="TopRight" :content="fileUnavailableTooltip(row.item.fileDetails)" style="display: inline; margin-right:5px;" >
                            <b-icon icon="info-circle-fill" variant="secondary" style="height: 15px; width: 15px; cursor: pointer; margin-right:-7px;"></b-icon>
                        </ejs-tooltip>
                        <b-button size="sm" variant="link" disabled :title="row.item.fileDetails.fileName">
                            {{ row.item.fileDetails.fileName }}
                        </b-button>
                    </div>
                </template>

                <template v-slot:cell(fileDetails.fileSize)="row">
                    <b-form-input :value="row.value" size="sm" readonly plaintext></b-form-input>
                </template>

                <template v-slot:cell(classification)="row">
                    <validation-provider :rules="{required_if: attachmentClassificationSelection == 'required'}"
                                         name="Classification"
                                         v-slot="vp1"
                                         v-if="modificationAllowed(row.item, 'inline') && isClassificationSelectable(row.item.classificationString) && fileAvailable(row.item.fileDetails)">
                        <select2 v-model="row.item.classificationString"
                                 placeholder="Select a classification"
                                 :options="possibleAttachmentClassifications(row.item.classificationString)"
                                 disable-search
                                 css-class="form-control form-control-sm">
                        </select2>
                        <b-form-invalid-feedback :state="vp1.errors[0] ? false : null">{{ vp1.errors[0] }}</b-form-invalid-feedback>
                    </validation-provider>
                    <b-form-input :value="row.item.classificationDisplayName" size="sm" readonly plaintext v-else></b-form-input>
                </template>

                <template v-slot:cell(description)="row">
                    <validation-provider :rules="{required_if: attachmentDescriptionInput == 'required' }"
                                         name="Description"
                                         v-slot="vp2"
                                         v-if="modificationAllowed(row.item, 'inline') && fileAvailable(row.item.fileDetails)">
                        <b-form-input v-model="row.item.descriptionString" size="sm"></b-form-input>
                        <b-form-invalid-feedback :state="vp2.errors[0] ? false : null">{{ vp2.errors[0] }}</b-form-invalid-feedback>
                    </validation-provider>
                    <b-form-input :value="row.item.descriptionString" size="sm" readonly plaintext v-else></b-form-input>
                </template>

                <template v-slot:cell(actions)="row">
                    <b-button-group>
                        <b-button size="sm" variant="outline-secondary" v-on:click="removeAttachment(row.item)" v-if="removeAllowed(row.item) && fileAvailable(row.item.fileDetails)">
                            Remove
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" v-on:click="modifyAttachmentAdditionalProps(row.item)" v-if="modificationAllowed(row.item, 'dialog') && fileAvailable(row.item.fileDetails)">
                            Modify
                        </b-button>
                    </b-button-group>
                </template>
            </b-table>
        </b-overlay>

            <validation-observer v-slot="{ passes }" slim>
                <b-modal v-model="additionalPropsData.show" 
                         size="md" title="Attachment Details" 
                         v-on:ok.prevent="passes(setAdditionalProps)"
                         v-on:shown="$emit('modalOpen', true)"
                         v-on:hidden="additionalPropsModalHidden">

                    <b-table striped bordered hover small fixed :items="additionalPropsData.files" :fields="additionalPropsData.tableFields">
                        <template v-slot:cell(classification)="row">
                            <validation-provider :rules="{required_if: attachmentClassificationSelection == 'required'}" name="Classification" v-slot="vp1">
                                <select2 v-model="row.item.classification"
                                         placeholder="Select a classification"
                                         :options="possibleAttachmentClassifications(row.item.classification)"
                                         disable-search
                                         css-class="form-control form-control-sm">
                                </select2>
                                <b-form-invalid-feedback :state="vp1.errors[0] ? false : null">{{ vp1.errors[0] }}</b-form-invalid-feedback>
                            </validation-provider>
                        </template>

                        <template v-slot:cell(description)="row">
                            <validation-provider :rules="{required_if: attachmentDescriptionInput == 'required' }" name="Description" v-slot="vp2">
                                <b-form-input v-model="row.item.description" size="sm"></b-form-input>
                                <b-form-invalid-feedback :state="vp2.errors[0] ? false : null">{{ vp2.errors[0] }}</b-form-invalid-feedback>
                            </validation-provider>
                        </template>
                    </b-table>
                    <b-form-row v-if="(additionalPropsData.mode == 'new') && includeAwaitingFurtherAttachments">
                        <b-form-group>
                            <b-form-checkbox name="awaitingFurtherAttachments"
                                             v-model="awaitingFurtherAttachments"
                                             switch>
                                Awaiting Further Attachments
                            </b-form-checkbox>
                        </b-form-group>
                    </b-form-row>
                </b-modal>
            </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import Resumable from 'resumablejs';

    import { ValidationProvider } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, required_if } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('required_if', required_if);
    localize('en', en);

    import ErrorsMixin from '../mixins/ErrorsMixin';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import UuidMixins from '../mixins/UuidMixins';

    Vue.use(BootstrapVue);
    Vue.component('validation-provider', ValidationProvider);

    export default {
        name: 'Attachments',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        model: {
            event: 'change',
            prop: 'attachments'
        },
        props: {
            apiControllerUrl: {
                type: String,
                default: null
            },
            fileDownloadUrl: {
                type: String,
                default: null
            },
            attachments: {
                type: Array,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                default: null
            },
            disabled: {
                type: Boolean,
                default: false
            },
            currentUserAccountId: {
                type: String,
                required: false
            },
            canAddAttachments: {
                type: Boolean,
                default: true
            },
            canModifyAttachmentTypes: {
                type: Array,
                default: null
            },
            canModifyAllAttachments: {
                type: Boolean,
                default: true
            },

            relatedTo: {
                type: String,
                default: null
            },
            relatedToId: {
                type: String,
                default: null
            },

            includeAwaitingFurtherAttachments: {
                type: Boolean,
                default: false
            },
            initialAwaitingFurtherAttachments: {
                type: Boolean,
                default: null
            },

            // allowed values are only: hidden|optional|required
            // the below 2 prop can then be used in this file like: this.attachmentClassificationSelection or attachmentClassificationSelection (in the markup)
            // and based on those values we will show/hide UI Elements, like if (attachmentClassificationSelection=='required') .... etc.
            attachmentClassificationSelection: {
                validator: function (value) {
                    return ['hidden', 'optional', 'required'].includes(value)
                },
                default: 'hidden'
            },
            attachmentDescriptionInput: {
                validator: function (value) {
                    return ['hidden', 'optional', 'required'].includes(value)
                },
                default: 'hidden'
            },

            includeOrderingClientColumn: {
                type: Boolean,
                default: false
            },

            maxAttachments: {
                default: null
            },

            legacyMode: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                currentAttachments: this.attachments ? this.attachments : [],
                uploadsInProgress: [],
                tableFields: [
                    {
                        key: 'fileDetails.fileName',
                        label: 'File Name',
                        tdClass: 'attachments-filename'
                    },
                    {
                        key: 'fileDetails.fileSize',
                        label: 'Size',
                        formatter: (value, key, item) => {
                            var strVal = '';
                            if (value > (10 * 1024 * 1024 * 1024)) {
                                strVal = Math.round(value / (1024 * 1024 * 1024)) + ' GB';
                            } else if (value > (10 * 1024 * 1024)) {
                                strVal = Math.round(value / (1024 * 1024)) + ' MB';
                            } else if (value > (10 * 1024)) {
                                strVal = Math.round(value / 1024) + ' KB';
                            } else {
                                strVal = value + ' Bytes';
                            }
                            if (item.fileDetails.uploadInProgress) {
                                strVal += ' (uploading)';
                            }
                            return strVal;
                        }
                    },


                ],
                newFiles: null,
                sanitizedNewFiles: null,
                awaitingFurtherAttachments: this.initialAwaitingFurtherAttachments,
                resumable: null,
                additionalPropsData: {
                    mode: 'new',
                    files: null,
                    attachment: null,
                    show: false,
                    tableFields: [
                        {
                            key: 'fileName',
                            label: 'File Name'
                        }
                    ]
                },
                customQueryParamsByFileUniqueIdentifier: [],
                suspendReload: false,
                showSpinner: false
            };
        },
        watch: {
            attachments: function () {
                var newAttachments = this.attachments ? this.attachments : [];
                this.updateAttachments(newAttachments);
            }
        },
        computed: {
            allowMultipleUploads: function () {
                if (this.maxAttachments == null) return true;
                return false;
            },
            canAddMoreAttachments: function () {
                if (this.disabled) return false;
                if (!this.canAddAttachments) return false;
                if (this.maxAttachments == null) return true;
                if (typeof (this.maxAttachments) === "number") {
                    if (this.currentAttachments == null) {
                        return (this.maxAttachments > 0);
                    }
                    return (this.maxAttachments > this.currentAttachments.length);
                }
                if (this.allAttachmentClassifications == null) return false;
                for (var classification in this.maxAttachments) {
                    if (this.allAttachmentClassifications.find(c => c.value == classification) == null) continue;
                    var maxAttachments = this.maxAttachments[classification];
                    if (this.currentAttachments == null) {
                        if (maxAttachments > 0) return true;
                    }
                    if (maxAttachments > this.currentAttachments.filter(a => a.classificationString == classification).length) return true;
                }
                return false;
            },
            standaloneMode: function () {
                return (this.relatedTo && this.relatedToId);
            },
            additionalPropsRequiredBeforeUpload: function () {
                if (this.standaloneMode) {
                    var possibleAttachmentClassifications = this.possibleAttachmentClassifications();
                    if (this.attachmentClassificationSelection == 'optional') {
                        if ((possibleAttachmentClassifications != null) && (possibleAttachmentClassifications.length > 0)) return true; //the user can either pick no classification or can pick one
                    }
                    if (this.attachmentClassificationSelection == 'required') {
                        if ((possibleAttachmentClassifications != null) && (possibleAttachmentClassifications.length > 1)) return true; //there is more than one classifications to choose from
                    }
                    if (this.attachmentDescriptionInput == 'optional' || this.attachmentDescriptionInput == 'required') return true;
                }
                return false;
            },
        },
        methods: {
            updateAttachments: function (newAttachments) {
                this.uploadsInProgress.forEach(uploadInProgress => {
                    if (uploadInProgress.uniqueIdentifier) {
                        var attachmentInProgress = newAttachments.find(f => f.fileDetails.uploadIdentifier == uploadInProgress.uniqueIdentifier);
                        if (attachmentInProgress) {
                            var index = newAttachments.indexOf(attachmentInProgress);
                            if (index > -1) {
                                newAttachments.splice(index, 1);
                            }
                        }
                    }
                });
                this.currentAttachments = newAttachments;
            },
            fileAvailable: function (fileDetails) {
                if (fileDetails.deletedByDataRetentionExecutionId != null || fileDetails.archivedByDataRetentionExecutionId != null)
                    return false;
                return true;
            },
            fileUnavailableTooltip: function (fileDetails) {
                var message = "";
                if (fileDetails.deletedByDataRetentionExecution != null)
                    message = "This file has been permanently deleted as per company data retention policies on the "
                        + this.formatDateTime(this.parseDateTime(fileDetails.deletedByDataRetentionExecution.completedDateTimeUTC), "DD/MM/yy")
                        + ", by rule: "
                        + fileDetails.deletedByDataRetentionExecution.dataRetentionRule.name;

                else if (fileDetails.archivedByDataRetentionExecution != null)
                    message = "This file has been archived as per company data retention policies on the "
                        + this.formatDateTime(this.parseDateTime(fileDetails.archivedByDataRetentionExecution.completedDateTimeUTC), "DD/MM/yy")
                        + ", by rule: "
                        + fileDetails.archivedByDataRetentionExecution.dataRetentionRule.name;
                else
                    message = "Error.";

                return message;
            },
            uploadFile: function () {

                if (this.newFiles) {
                    var clearNewFiles = true;
                    var newFiles = this.newFiles;
                    this.sanitizedNewFiles = [];
                    
                    if (!(newFiles instanceof Array)) newFiles = [newFiles];

                    // loop over our array of files to be uploaded
                    for (var i = 0; i < newFiles.length; i++) {

                        // check if we are dealing with any NOT allowed files
                        if (
                            (newFiles[i].type.startsWith('application') && newFiles[i].name.toLowerCase().endsWith('exe')) ||
                            (newFiles[i].type.startsWith('text/html') && newFiles[i].name.toLowerCase().endsWith('html')) ||
                            (newFiles[i].type.startsWith('text/html') && newFiles[i].name.toLowerCase().endsWith('htm')) ||
                            (newFiles[i].type.startsWith('text/javascript') && newFiles[i].name.toLowerCase().endsWith('js')) ||
                            (newFiles[i].name.toLowerCase().endsWith('ps1')) // power shell script files did not come up with a file type
                        ) {
                            
                            this.$bvToast.toast(`Prevented file from upload: ${newFiles[i].name}`, {
                                title: 'File upload warning',
                                autoHideDelay: 3000,
                                variant: 'warning'
                            });
                            
                        } else {
                            // push files which are allowed to be uploaded
                            this.sanitizedNewFiles.push(newFiles[i]);
                        }
                    }
                    
                    if (this.sanitizedNewFiles.length) {
                        if (this.resumable) {
                            if (this.additionalPropsRequiredBeforeUpload) {
                                clearNewFiles = false;
                                this.additionalPropsData.mode = 'new';
                                this.additionalPropsData.files = [];
                                this.sanitizedNewFiles.forEach(newFile => {
                                    this.additionalPropsData.files.push({
                                        uniqueIdentifier: this.resumableFileGenerateUniqueIdentifier(newFile),
                                        fileName: newFile.webkitRelativePath || newFile.relativePath || newFile.fileName || newFile.name,
                                        classification: null,
                                        description: null                                
                                    });
                                });
                                this.additionalPropsData.show = true;
                            } else {
                                this.setDefaultAdditionalProps(this.sanitizedNewFiles);
                                this.resumable.addFiles(this.sanitizedNewFiles);
                            }
                        } else {
                            var previousUploadInProgress = this.uploadsInProgress.length > 0;
                            var filesToBeUploaded = this.sanitizedNewFiles.length;
                            this.sanitizedNewFiles.forEach(newFile => {
                                var currentErrors = [];
                                var uploadInProgress = {
                                    name: newFile.name,
                                    progress: 0
                                };
                                this.uploadsInProgress.push(uploadInProgress);
                                if (!previousUploadInProgress) this.$emit('uploadInProgress', true);
                                previousUploadInProgress = false;
                                filesToBeUploaded = filesToBeUploaded - 1;
                                let formData = new FormData();
                                formData.append('files', newFile);

                                axios
                                    .post(this.$urlHelper.getApiUrl('file'), formData,
                                        {
                                            headers: {
                                                'Content-Type': 'multipart/form-data'
                                            },
                                            onUploadProgress: progressEvent => {
                                                var progress = (progressEvent.loaded / progressEvent.total) * 100;
                                                if (progress > 100) progress = 100;
                                                uploadInProgress.progress = progress;
                                            }
                                        }
                                    ).then(response => {
                                        var files = response.data;
                                        files.forEach(file => {
                                            this.currentAttachments.push({
                                                isNew: true,
                                                fileId: file.fileId,
                                                fileDetails: file
                                            });
                                        });
                                        this.$emit('change', this.currentAttachments);
                                    })
                                    .catch(ex => {
                                        if (ex.response && ex.response.status == 404 && ex.response.data && ex.response.data.indexOf('404.13')) {
                                            currentErrors.push('File is too large');
                                        } else {
                                            currentErrors = this.handleAxiosException(ex);
                                        }
                                    })
                                    .finally(() => {
                                        if (currentErrors.length != 0) {
                                            this.showErrorsToast(currentErrors);
                                        }
                                        var index = this.uploadsInProgress.indexOf(uploadInProgress);
                                        if (index > -1) {
                                            this.uploadsInProgress.splice(index, 1);
                                        }
                                        if (filesToBeUploaded == 0) {
                                            if (this.uploadsInProgress.length == 0) this.$emit('uploadInProgress', false);
                                        }
                                    });
                            });
                        }
                    }
                    if (clearNewFiles) {
                        setTimeout(() => {
                            this.newFiles = null;
                            this.sanitizedNewFiles = null;
                        }, 100);
                    }
                }
            },
            setDefaultAdditionalProps: function (newFiles) {
                var defaultAttachmentClassification = null;
                var possibleAttachmentClassifications = this.possibleAttachmentClassifications();
                if ((this.attachmentClassificationSelection == 'required') &&
                    (possibleAttachmentClassifications != null) &&
                    (possibleAttachmentClassifications.length == 1))
                {
                    defaultAttachmentClassification = possibleAttachmentClassifications[0].value;
                }
                if (defaultAttachmentClassification) {
                    newFiles.forEach(file => {
                        this.customQueryParamsByFileUniqueIdentifier[this.resumableFileGenerateUniqueIdentifier(file)] = { classification: defaultAttachmentClassification };
                    });
                }
            },
            setAdditionalProps: function () {
                if (this.additionalPropsData.mode == 'new') {
                    this.additionalPropsData.files.forEach(file => {
                        var customQueryParams = {};
                        if (file.classification) customQueryParams.classification = file.classification;
                        if (file.description) customQueryParams.classification = file.description;
                        this.customQueryParamsByFileUniqueIdentifier[file.uniqueIdentifier] = customQueryParams;
                    });
                    this.resumable.addFiles(this.sanitizedNewFiles);
                    this.additionalPropsData.show = false;
                } else if (this.additionalPropsData.mode == 'edit') {
                    var attachment = this.additionalPropsData.attachment;
                    var file = this.additionalPropsData.files[0];

                    axios
                        .put(this.$urlHelper.getApiUrl('attachment', 'updateClassification', [attachment.attachmentId]), file.classification,
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            }
                        )
                        .then(() => { 
                            this.additionalPropsData.show = false;
                            attachment.classificationString = file.classification;
                            attachment.classificationDisplayName = this.allAttachmentClassifications.find(c => c.value == file.classification).text;
                            this.$emit('change', this.currentAttachments);
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        })
                        .finally(() => {
                            this.suspendReload = false;
                        });
                }
            },
            additionalPropsModalHidden: function () {
                this.$emit('modalOpen', false);
                this.additionalPropsData.attachment = null;
                this.additionalPropsData.files = null;
                if (this.additionalPropsData.mode == 'new') {
                    setTimeout(() => {
                        this.newFiles = null;
                        this.sanitizedNewFiles = null;
                    }, 100);
                }
                this.suspendReload = false;
            },
            resumableFileGenerateUniqueIdentifier: function (file) {
                if (file) {
                    if (!file.uniqueIdentifier) file.uniqueIdentifier = this.newUuid();
                    return file.uniqueIdentifier;
                }
                return this.newUuid();
            },
            resumbleFileAdded: function (file) {
                var uploadInProgress = {
                    uniqueIdentifier: file.uniqueIdentifier,
                    name: file.fileName,
                    progress: 0
                };
                var previousUploadInProgress = this.uploadsInProgress.length > 0;
                this.uploadsInProgress.push(uploadInProgress);
                this.resumable.upload();
                if (!previousUploadInProgress) this.$emit('uploadInProgress', true);
            },
            resumbleFileProgress: function (file) {
                var uploadEntry = this.uploadsInProgress.find(f => f.uniqueIdentifier == file.uniqueIdentifier);
                if (uploadEntry) uploadEntry.progress = file.progress() * 100.0;
            },
            resumbleFileSuccess: function (file, message) {
                this.removeResumableFile(file);
                var response = JSON.parse(message);
                var newEntry = response;
                if (!newEntry.fileDetails) {
                    newEntry = {
                        isNew: true,
                        fileId: response.fileId,
                        fileDetails: response
                    }
                }
                var existingEntry = this.currentAttachments.find(f => f.fileId == newEntry.fileId);
                if (existingEntry) { //this can happen if while an upload is in progress the list of attachments gets refreshed
                    var index = this.currentAttachments.indexOf(existingEntry);
                    if (index > -1) {
                        this.currentAttachments.splice(index, 1);
                    }
                }
                this.currentAttachments.push(newEntry);
                this.$emit('change', this.currentAttachments);
            },
            resumbleFileError: function (file, message) {
                this.removeResumableFile(file);
                this.showErrorsToast([message]);
            },
            resumableCustomQueryParameters: function (file) {
                var customQueryParams = {};
                if (this.relatedTo && this.relatedToId) {
                    customQueryParams = {
                        relatedTo: this.relatedTo,
                        relatedToId: this.relatedToId
                    };
                    if (this.awaitingFurtherAttachments != null) {
                        customQueryParams.awaitingFurtherAttachments = this.awaitingFurtherAttachments;
                    }
                }
                if (this.customQueryParamsByFileUniqueIdentifier[file.uniqueIdentifier]) {
                    customQueryParams = {
                        ...customQueryParams,
                        ...this.customQueryParamsByFileUniqueIdentifier[file.uniqueIdentifier]
                    };
                }
                return customQueryParams;
            },
            removeResumableFile: function (file) {
                this.resumable.removeFile(file);
                if (this.customQueryParamsByFileUniqueIdentifier[file.uniqueIdentifier]) delete this.customQueryParamsByFileUniqueIdentifier[file.uniqueIdentifier];
                var uploadEntry = this.uploadsInProgress.find(f => f.uniqueIdentifier == file.uniqueIdentifier);
                if (uploadEntry) {
                    var index = this.uploadsInProgress.indexOf(uploadEntry);
                    if (index > -1) {
                        this.uploadsInProgress.splice(index, 1);
                    }
                }
                if (this.uploadsInProgress.length == 0) this.$emit('uploadInProgress', false);
            },
            modificationAllowed: function (attachment, uiType) {
                if (this.disabled) return false;
                if (this.standaloneMode && (uiType == 'inline')) return false;
                if (!this.standaloneMode && (uiType == 'dialog')) return false;
                if (this.canModifyAttachmentTypes == []) return false;
                if (attachment) {
                    if (attachment.fileDetails.uploadInProgress) return false;
                    if (this.canModifyAttachmentTypes != null) {
                        if (this.canModifyAttachmentTypes.indexOf(attachment.attachmentType) < 0) return false;
                    }
                    if (!this.canModifyAllAttachments) {
                        if (!this.currentUserAccountId) return false;
                        if (attachment.fileDetails.createdByUserAccountId != this.currentUserAccountId) return false;
                    }
                    if (uiType == 'dialog') {
                        if ((this.attachmentClassificationSelection == 'hidden') && (this.attachmentDescriptionInput == 'hidden')) return false;
                        if ((this.attachmentClassificationSelection != 'hidden') && (this.attachmentDescriptionInput == 'hidden')) {
                            if ((this.allAttachmentClassifications == null) || (this.possibleAttachmentClassifications(attachment.classificationString).length < 2)) return false;
                        }
                    }
                }
                return true;
            },
            modifyAttachmentAdditionalProps: function (attachment) {
                this.suspendReload = true;
                this.additionalPropsData.mode = 'edit';
                this.additionalPropsData.attachment = attachment;
                this.additionalPropsData.files = [{
                    fileName: attachment.fileDetails.fileName,
                    classification: attachment.classificationString,
                    description: attachment.description
                }];
                this.additionalPropsData.show = true;
            },
            removeAllowed: function (attachment) {
                if (this.disabled) return false;
                if (this.canModifyAttachmentTypes == []) return false;
                if (attachment) {
                    if (attachment.fileDetails.uploadInProgress) return false;
                    if (this.canModifyAttachmentTypes != null) {
                        if (this.canModifyAttachmentTypes.indexOf(attachment.attachmentType) < 0) return false;
                    }
                    if (!this.canModifyAllAttachments) {
                        if (!this.currentUserAccountId) return false;
                        if (attachment.fileDetails.createdByUserAccountId != this.currentUserAccountId) return false;
                    }
                }
                return true;
            },
            removeAttachment: function (attachment) {
                this.suspendReload = true;
                this.$emit('modalOpen', true);
                var index = this.currentAttachments.indexOf(attachment);
                if (index > -1) {
                    if (this.standaloneMode) {
                        this.$bvModal.msgBoxConfirm(
                            [
                                this.$createElement(
                                    'div',
                                    {
                                        domProps:
                                        {
                                            innerHTML: "<p>Are you sure you want to remove the attachment <strong>" + attachment.fileDetails.fileName + "</strong>?</p>"
                                        }
                                    }
                                )
                            ],
                            {
                                title: 'Remove Attachment',
                                size: 'md',
                                okVariant: 'success',
                                okTitle: 'Yes, Remove Attachment',
                                cancelTitle: 'No, Keep Attachment',
                                centered: true
                            })
                            .then(value => {
                                if (value) {
                                    axios
                                        .delete(this.$urlHelper.getApiUrl('attachment', attachment.attachmentId))
                                        .then(() => {
                                            this.currentAttachments.splice(index, 1);
                                            this.$emit('change', this.currentAttachments);
                                        })
                                        .catch(ex => {
                                            var currentErrors = this.handleAxiosException(ex);
                                            this.showErrorsToast(currentErrors);
                                        })
                                        .finally(() => {
                                            this.suspendReload = false;
                                            // the 'yes' case in of the delete dialog
                                            this.$emit('modalOpen', false);
                                        });
                                }
                                // the 'no' case of the delete dialog
                                this.$emit('modalOpen', false);
                            });
                    } else {
                        this.currentAttachments.splice(index, 1);
                        this.suspendReload = false;
                        this.$emit('change', this.currentAttachments);
                    }
                } else {
                    this.suspendReload = false;
                }
                
            },
            possibleAttachmentClassifications: function (currentClassification = null) {
                if (this.allAttachmentClassifications == null) return null;
                if (this.maxAttachments == null) return this.allAttachmentClassifications;
                if (typeof (this.maxAttachments) === "number") return this.allAttachmentClassifications;
                var possibleAttachmentClassificationOptions = [];
                for (var classification in this.maxAttachments) {
                    var classificationOption = this.allAttachmentClassifications.find(c => c.value == classification);
                    if (!classificationOption) continue;
                    if (classification == currentClassification) possibleAttachmentClassificationOptions.push(classificationOption);
                    else {
                        var maxAttachments = this.maxAttachments[classification];
                        if (this.currentAttachments == null) {
                            if (maxAttachments > 0) possibleAttachmentClassificationOptions.push(classificationOption);
                        }
                        else if (maxAttachments > this.currentAttachments.filter(a => a.classificationString == classification).length) possibleAttachmentClassificationOptions.push(classificationOption);
                    }
                }
                return possibleAttachmentClassificationOptions;
            },
            isClassificationSelectable: function (classification) {
                if (classification == null) return true;
                var matchingClassification = this.allAttachmentClassifications.find(c => c.value == classification);
                return (matchingClassification != null);
            },
            reload: function (showSpinner = false) {
                if (!this.suspendReload) {
                    if ((this.relatedTo == "WorkItem") && this.relatedToId) {
                        this.showSpinner = showSpinner;
                        return axios
                            .get(this.$urlHelper.getApiUrl('attachment', 'byworkitem', [this.relatedToId]))
                            .then(response => {
                                if (!this.suspendReload) {
                                    var newAttachments = response.data ? response.data : [];
                                    this.updateAttachments(newAttachments);
                                }
                            })
                            .catch(ex => {
                                var currentErrors = this.handleAxiosException(ex);
                                this.showErrorsToast(currentErrors);
                            })
                            .finally(() => {
                                this.showSpinner = false;
                            });
                    }
                }
                return Promise.resolve();
            }
        },
        mounted: function () {
            if (this.attachmentClassificationSelection == "optional" || this.attachmentClassificationSelection == "required") {
                this.tableFields.push({
                    key: 'classification',
                    label: 'Classification',
                    thStyle: { width: '15rem' }
                });
                this.additionalPropsData.tableFields.push({
                    key: 'classification',
                    label: 'Classification',
                    thStyle: { width: '15rem' }
                });
            }

            if (this.includeOrderingClientColumn) {
                this.tableFields.push({
                    key: 'orderingClientName',
                    label: 'Ordering Client',
                    thStyle: { width: '18rem' },
                    sortable: true
                });
            }

            if (this.attachmentDescriptionInput == "optional" || this.attachmentDescriptionInput == "required") {
                this.tableFields.push({
                    key: 'description',
                    label: 'Description',
                    thStyle: { width: '18rem' }
                });
                this.additionalPropsData.tableFields.push({
                    key: 'description',
                    label: 'Description',
                    thStyle: { width: '18rem' }
                });
            }
            if (this.removeAllowed()) {
                this.tableFields.push({
                    key: 'actions',
                    label: 'Actions'
                });
            }

            if (!this.legacyMode) {
                this.resumable = new Resumable({
                    target: this.$urlHelper.getApiUrl('file', 'resumable'),
                    testTarget: this.$urlHelper.getApiUrl('file', 'resumable'),
                    query: this.resumableCustomQueryParameters,
                    simultaneousUploads: 1,
                    maxChunkRetries: 1,
                    testChunks: true,
                    generateUniqueIdentifier: this.resumableFileGenerateUniqueIdentifier
                });
                if (this.resumable.support) {
                    this.resumable.on("fileAdded", this.resumbleFileAdded);
                    this.resumable.on("fileProgress", this.resumbleFileProgress);
                    this.resumable.on("fileSuccess", this.resumbleFileSuccess);
                    this.resumable.on("fileError", this.resumbleFileError);
                } else {
                    this.resumable = null;
                }
            }
        }
    }
</script>

<style scoped>
    >>> .attachments-filename {
        overflow: hidden;
        word-break: break-all;
        max-height: 2rem;
        display: inline-block;
    }
</style>